import React, { useMemo } from 'react';
import { IElement } from '@shared/models/project.model';
import ElementQuantities from './components/ElementQuantities';
import ElementComments from './components/ElementComments';
import { ContentCoverageSection } from './components/ContentCoverageSection';
import { ElementCoreSettings } from './components/ElementCoreSettings/ElementCoreSettings';
import {
  getCategoryIdsByTypes,
  getCategoryType,
  isSystemCategoryElement,
} from '@shared/helpers/element-category.helpers';
import { isGenerated } from '@shared/helpers/element.helpers';
import {
  GeneratedElementChildrenSettings,
  useGeneratedProductElementChildren,
} from './components/GeneratedElementChildrenSettings';
import { SidePanel } from '@ui/layout';
import { isEnvironment } from '@shared/helpers/environment.helpers';

type ElementSidepanelContentProps = {
  /**
   * Element node for the side panel. Callers should pass the instance from
   * {@link useResolvedVersion} (e.g. via {@link useResolvedElement} in {@link ProjectSidePanel})
   * so proposal changes are reflected consistently.
   */
  selectedElement: IElement;
};

export const ElementSidePanelContent: React.FC<
  ElementSidepanelContentProps
> = ({ selectedElement }) => {
  const disabled = isGenerated(selectedElement);
  const generatedProductElementChildren =
    useGeneratedProductElementChildren(selectedElement);

  const categoryIds = useMemo(
    () => getCategoryIdsByTypes(getCategoryType(selectedElement.category_id)),
    [selectedElement.category_id],
  );

  return (
    <SidePanel.Content disabled={disabled}>
      <SidePanel.Content.Section>
        <ElementCoreSettings
          element={selectedElement}
          includeCategories={categoryIds}
        />
      </SidePanel.Content.Section>
      <SidePanel.Content.Divider />
      <SidePanel.Content.Section>
        <ElementQuantities element={selectedElement} />
      </SidePanel.Content.Section>

      <SidePanel.Content.Divider />

      {!isEnvironment('production') &&
        isSystemCategoryElement(selectedElement) && (
          <>
            <ContentCoverageSection element={selectedElement} />
            <SidePanel.Content.Divider />
          </>
        )}

      {generatedProductElementChildren.length > 0 && (
        <SidePanel.Content.Section>
          <GeneratedElementChildrenSettings
            element={selectedElement}
            generatedProductElementChildren={generatedProductElementChildren}
          />
        </SidePanel.Content.Section>
      )}

      <SidePanel.Content.Section>
        <ElementComments elementId={selectedElement.id} />
      </SidePanel.Content.Section>
    </SidePanel.Content>
  );
};
